// Corpus-config data layer (PRD 08 Slice C3). TanStack Query hooks for
// the dashboard Settings form.

import { apiFetch } from "../../lib/apiClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CORPUS_CONFIG_DEFAULTS,
  type CorpusConfig,
} from "../../data/types";


const corpusKey = (corpusId: string) => ["corpus-config", corpusId] as const;

export function useCorpusConfig(corpusId: string) {
  return useQuery<CorpusConfig, Error>({
    queryKey: corpusKey(corpusId),
    queryFn: async () => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/config`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) throw new Error(`config fetch failed: ${r.status}`);
      return (await r.json()) as CorpusConfig;
    },
    placeholderData: CORPUS_CONFIG_DEFAULTS,
    staleTime: 30_000,
  });
}

export function usePutCorpusConfig(corpusId: string) {
  const qc = useQueryClient();
  return useMutation<CorpusConfig, Error, CorpusConfig>({
    mutationFn: async (body) => {
      const r = await apiFetch(
        `/corpora/${encodeURIComponent(corpusId)}/config`,
        {
          method: "PUT",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`config save failed (${r.status}): ${detail}`);
      }
      return (await r.json()) as CorpusConfig;
    },
    // Optimistic update so the form reflects the new value immediately.
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: corpusKey(corpusId) });
      const previous = qc.getQueryData<CorpusConfig>(corpusKey(corpusId));
      qc.setQueryData(corpusKey(corpusId), next);
      return { previous };
    },
    onError: (_e, _next, ctx) => {
      const previous = (ctx as { previous?: CorpusConfig } | undefined)
        ?.previous;
      if (previous) qc.setQueryData(corpusKey(corpusId), previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: corpusKey(corpusId) });
    },
  });
}
